import { FIELDS, FIELD_KEYS, REQUIRED_FIELD_KEYS, fieldByKey } from './fields'
import { transformValue } from './transform'
import type { ColumnMapping, ImportError, MappedRows, ParsedTable } from './types'

/** Popisky polí pro chybové hlášky (i18n řeší UI, tohle je fallback do dat). */
const LABELS: Record<string, string> = {
    code: 'Kód vstupenky',
    priceId: 'ID ceny',
    priceName: 'Název ceny',
    priceNoDph: 'Cena bez DPH',
    priceDph: 'DPH',
    priceAmount: 'Částka',
    currency: 'Měna',
    ticketsetId: 'ID sady',
    ticketsetName: 'Název sady',
    orderId: 'Číslo objednávky',
    paymentSource: 'Zdroj platby',
    paymentType: 'Typ platby',
    orderDate: 'Datum objednávky',
    firstName: 'Jméno',
    lastName: 'Příjmení',
    phone: 'Telefon',
    email: 'E-mail'
}

/** Pole, jejichž výběr z existujících entit doplní i párové ID pole. */
const PAIRED_IDS: readonly [string, string][] = [
    ['ticketsetName', 'ticketsetId'],
    ['priceName', 'priceId']
]

/**
 * Aplikuje mapování na tabulku: vytáhne hodnoty, znormalizuje je a zvaliduje.
 *
 * Vrací řádky i chyby zároveň — import je atomický, takže volající při
 * neprázdných chybách vůbec neodesílá, ale potřebuje je uživateli ukázat
 * všechny najednou.
 */
export function applyMapping(table: ParsedTable, mapping: ColumnMapping): MappedRows {
    const rows: Record<string, string>[] = []
    const errors: ImportError[] = []

    table.rows.forEach((rawRow, idx) => {
        const sourceRow = table.firstDataRow + idx
        const out: Record<string, string> = {}

        for (const field of FIELDS) {
            const source = mapping[field.key] ?? { kind: 'empty' as const }

            let raw = ''
            if (source.kind === 'column') raw = rawRow[source.index] ?? ''
            else if (source.kind === 'value' || source.kind === 'existing') raw = source.value

            const { value, error } = transformValue(field, raw)
            if (error) {
                errors.push({ row: sourceRow, message: `${LABELS[field.key] ?? field.key}: ${error}` })
            }
            out[field.key] = value
        }

        // Výběr existující sady/ceny přinese i její bzuco_id. Bez něj by backend
        // sice napároval podle názvu, ale nově zakládané entity by dostaly 0.
        for (const [nameKey, idKey] of PAIRED_IDS) {
            const source = mapping[nameKey]
            if (source?.kind === 'existing' && source.bzucoId != null) {
                out[idKey] = String(source.bzucoId)
            }
        }

        for (const key of REQUIRED_FIELD_KEYS) {
            if (!out[key]) {
                errors.push({ row: sourceRow, message: `${LABELS[key] ?? key}: povinná hodnota chybí` })
            }
        }

        out.sourceRow = String(sourceRow)
        rows.push(out)
    })

    return { rows, errors }
}

/**
 * Řádky → poziční JSON pro `admin.ticket.upload`.
 *
 * Pořadí MUSÍ odpovídat `ImportColumnEnum` na backendu; 18. položka je číslo
 * řádku ve zdroji, aby chybové hlášky ukazovaly na správné místo v souboru.
 */
export function rowsToJson(rows: Record<string, string>[]): string {
    return JSON.stringify(rows.map(r => [...FIELD_KEYS.map(k => r[k] ?? ''), r.sourceRow ?? '']))
}

/** Chybí-li povinné pole ve zdroji, nemá smysl pouštět uživatele dál. */
export function missingRequiredFields(mapping: ColumnMapping): string[] {
    return REQUIRED_FIELD_KEYS.filter(key => {
        const source = mapping[key]
        if (!source || source.kind === 'empty') return true
        if (source.kind === 'value' || source.kind === 'existing') return source.value.trim() === ''
        return false
    })
}

/** Validace pevné hodnoty ve chvíli zadání v UI. */
export function validateFixedValue(fieldKey: string, value: string): string | null {
    const field = fieldByKey(fieldKey)
    if (!field) return null
    if (!field.allowFixedValue) return 'Pevná hodnota není u tohoto pole povolená'
    if (value.trim() === '') return field.required ? 'Povinná hodnota' : null
    return transformValue(field, value).error
}

export { LABELS as FIELD_LABELS }
